<template>
  <div class="col-12 col-md-3 pt-3">
    <div class="col-12" v-if="!$store.state.isMobileScreen && $store.state.settings.showLiveStream">
      <LiveStreamComponent />
    </div>

    <div class="px-1 betslip-wrapper" :class="{ 'betslip-wrapper__open': isOpen, 'betslip-wrapper__closed': !isOpen }">
      <div id="test" ref="targetElement"></div>
      <div class="betslip">
        <betslip-tabs :current-tab="currentTab" @change="changeTab" />
        <template v-if="betslips.length == 0">
          <div class="bet-content">
            <div class="betslip-empty">
              <img src="@/public/img/ticket.svg" alt="" />
              {{ getTranslatedText(`betslip is empty`) }}
            </div>
          </div>
        </template>

        <template v-else>
          <betslip-content :betslips="betslips" :current-tab="currentTab" :invalid-bets="invalidBetslipId"
            :editing-amount="editingAmount" :temp-amount="tempAmount" @remove="removeBetslip"
            @update-temp-amount="handleUpdateTempAmount" @save-amount="updateAmountOnBetItem"
            @start-edit="startEditAmount" @change-each-way="handleChangeEachWay" />

          <betslip-summary-with-tax v-if="wagerTax > 0" :current-tab="currentTab" :bet-amount.sync="betsAmount"
            @change-stake="handleChangeStakeDebounced"
            @change-each-way-multiple="handleChangeEachWayMultip" />

          <betslip-summary v-else :current-tab="currentTab" :bet-amount.sync="betsAmount"
            @change-stake="handleChangeStakeDebounced"
            @change-each-way-multiple="handleChangeEachWayMultip" />

          <betslip-actions :current-tab="currentTab" :is-submit-data="isSubmitData" @book-now="bookNow"
            @remove-all="removeAllBetslip" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import languageMixins from "@/mixins/language/language-mixins";
import calculate from "@/mixins/calculate/calculate"
import {
  compareDateTime,
  compareDates,
  formatDateTimeZone,
  debounce,
  fixedNumber,
  isValidBetAmount,
  isStakeInRange,
  parseStakeAmount,
} from "@/utils/utils";
import EventBus from "@/utils/EventBus";
import utilsMixins from "@/mixins/utils/utils-mixins";
import indexService from "@/services/index.service";
import { alert } from "@/composables/useAlert";
import { modal } from "@/composables/useModal";
import LiveStreamComponent from "@/components/livestream/index.vue"
import BetslipTabs from './components/BetslipTabs.vue';
import BetslipContent from './components/BetslipContent.vue';
import BetslipSummary from './components/BetslipSummary.vue';
import BetslipActions from './components/BetslipActions.vue';
import BetslipSummaryWithTax from './components/BetslipSummaryWithTax.vue';
import { buildTransactionBet } from '@/utils/betslip';
import { isBetClosedOnBetslip } from '@/utils/betting';

const TAB_SINGLE = 'SINGLE';
const TAB_MULTIPLE = 'MULTIPLE';

export default {
  mixins: [languageMixins, calculate, utilsMixins],
  components: {
    LiveStreamComponent,
    BetslipTabs,
    BetslipContent,
    BetslipSummary,
    BetslipActions,
    BetslipSummaryWithTax
  },
  data() {
    return {
      isOpen: false,
      isSubmitData: false,
      editingAmount: {},
      tempAmount: {},
      handleChangeStakeDebounced: null,
    };
  },

  created() {
    this.handleChangeStakeDebounced = debounce((amount) => this.handleUpdateTotalStakeMultiple(amount), 300);
    EventBus.$on("open-betslip", (isOpen) => this.hanleOpenBetslip(isOpen));
    EventBus.$on("toggle-betslip", (isOpen) => {
      this.isOpen = isOpen;
    });
  },

  mounted() {
    EventBus.$on('refresh', this.handleRefresh)
  },

  beforeUnmount() {
    EventBus.$off("open-betslip");
    EventBus.$off('refresh', this.handleRefresh)
  },

  watch: {
    'betslips.length': {
      handler(newCount) {
        window.parent.postMessage({ type: "bets-count-changed", count: newCount }, "*");
      },
      immediate: true
    },
    betslips: {
      handler(betslips) {
        // Stale EW-multiple after mixing non-EW markets (forecast/tricast) or rehydrate
        const isOnlyEachWay = betslips.length > 0 && betslips.every((x) => x.isEW && x.runners >= 5);
        if (!isOnlyEachWay && this.eachWayBetMultiple) {
          this.$store.dispatch('updateEachWayBetMultiple', false);
        }
      },
      deep: true,
      immediate: true,
    },
  },

  computed: {
    isLogin() {
      return this.$store.state.isLogin;
    },
    invalidBets() {
      return this.$store.getters.invalidBets;
    },
    betslips() {
      const game = this.$store.state.settings.currentGame ?? "hr";
      return this.$store.state.betslips.filter(bet => bet.gameType === game);
    },
    betDefault() {
      return this.$store.state.settings.betDefault;
    },
    wagerTax() {
      return this.$store.state.settings.wagerTax;
    },
    wagerTaxType() {
      return this.$store.state.settings.wagerTaxType;
    },
    betMin() {
      return this.$store.state.settings.betMin;
    },
    isShowEachWayMultiple() {
      return this.$store.getters.isShowEachWayMultiple;
    },
    betMax() {
      return this.$store.state.settings.betMax;
    },
    maxPayout() {
      return this.$store.state.settings.maxPayout;
    },
    logoBet() {
      const game = this.$store.state.settings.currentGame;
      return require(`@/public/img/icon-${game === 'hr' ? 'horse' : 'dog'}.png`);
    },

    currentTab() {
      return this.$store.state.settings.currentTab;
    },
    betsAmount() {
      return this.$store.state.totalStakeMultiple;
    },
    totalStakeMulti() {
      return this.$store.getters.totalStake;
    },
    eachWayBetMultiple() {
      return this.$store.state.eachWayBetMultiple;
    },

    invalidBetslipId() {
      let invalidBets = [];
      const currentRace = this.$store.state.race;
      const nextupData = this.$store.state.nextupStore.nextupData;

      this.betslips.forEach((bet) => {
        if (compareDateTime(bet.raceTime, Date.now(), this.$store.state.settings.timeZone) <= 0) {
          if (bet.fieldId) invalidBets.push(bet.fieldId);
          if (bet.selectionId) invalidBets.push(bet.selectionId);
          invalidBets.push(bet.raceId);
        }

        if (isBetClosedOnBetslip(bet, currentRace, nextupData)) {
          if (bet.fieldId) invalidBets.push(bet.fieldId);
          if (bet.selectionId) invalidBets.push(bet.selectionId);
          invalidBets.push(bet.raceId);
        }
      });
      // Check same race khi ở tab MULTIPLE
      if (this.currentTab === TAB_MULTIPLE) {
        const raceDuplicate = this.betslips.filter((item, index, array) => {
          return array.some((otherItem, otherIndex) => {
            return index !== otherIndex && item.raceId === otherItem.raceId;
          });
        });
        const duplicateIds = raceDuplicate.flatMap(item => {
          if (item.listHorse?.length) {
            return item.listHorse.map(horse => horse.fieldId);
          }
          if (item.selectionId) return [item.selectionId];
          return item.fieldId ? [item.fieldId] : [];
        });
        invalidBets.push(...duplicateIds);
      }
      return invalidBets;
    },
  },
  methods: {
    handleRefresh() {
      this.$forceUpdate();
    },

    redirectPage() {
      this.$router.push({ path: "/history/", query: this.$route.query });
    },

    changeTab(tabId) {
      this.$store.dispatch("updateSettingByName", { name: "currentTab", value: tabId });
    },

    showDate(date) {
      let d = formatDateTimeZone(date, this.timeZone)
      return compareDates(date, Date.now(), this.timeZone) == 0 ?
        `${this.getTranslatedText('today')} ${d.hour}:${d.minutes}` : `${d.day}-${d.month}-${d.year} ${d.hour}:${d.minutes}`;
    },

    hanleOpenBetslip(isOpen) {
      this.isOpen = !isOpen;
    },

    removeAllBetslip() {
      this.$store.commit("REMOVE_ALL_BETSLIP");
    },

    removeBetslip(item) {
      this.$store.commit("REMOVE_BETSLIP", item);
    },

    buildBetPayload() {
      const isMultiStake = this.betslips.length === 1 && this.currentTab === TAB_MULTIPLE;
      // EW multiple only valid when every leg is win_or_ew; drop stale eachWayBet after mix markets (e.g. forecast)
      const eachWayMultipleActive = this.$store.getters.isEachWayMultipleActive;
      const bets = this.betslips.map((bet) => buildTransactionBet({
        ...bet,
        eachWayBet: this.currentTab === TAB_MULTIPLE ? eachWayMultipleActive : bet.eachWayBet,
      }, {
        isMultiStake,
        totalStakeMulti: this.totalStakeMulti,
        wagerTax: this.wagerTax,
      }));

      const type = this.currentTab === TAB_SINGLE || bets.length === 1 ? 'single' : 'multi';
      const total = this.currentTab === TAB_SINGLE ? 0 : this.totalStakeMulti;

      return { type, bets, total };
    },

    showSuccessModal(res) {
      modal.show({
        isVisible: true,
        message: `${this.getTranslatedText('betslip')}: ${res.data.transaction.id}`,
        header: this.getTranslatedText('successful bets'),
        title: this.getTranslatedText('bet confirmation'),
        isOk: false,
        textButton: this.getTranslatedText('continue beting'),
        textCustom: this.getTranslatedText('click here to view your history!'),
        onCustom: null,
        onRedirectPage: () => this.redirectPage(),
      });
    },

    handleBookNowSuccess(res) {
      this.handleScroll();
      alert.showSuccess(res?.data.message);
      this.$store.dispatch('updateEachWayBetMultiple', false);
      this.showSuccessModal(res);
      this.removeAllBetslip();

      const amount = this.handleCalculateDefaultAmount(this.betMin, this.betDefault);
      this.$store.commit('UPDATE_TOTAL_STAKE_MULTIPLE', amount);

      parent.postMessage({ action: 'print', type: 'print-horse-betslip', data: JSON.stringify(res.data.print_data) }, '*');
      parent.postMessage({ command: 'refreshbalance' }, '*');
    },

    handleBookNowError(res) {
      this.handleScroll();
      if (res.dataError?.length > 0) {
        modal.show({
          isVisible: true,
          message: this.getTranslatedText(res.message),
          isOk: true,
        });
      } else {
        const message = res.message || this.getTranslatedText('No response data');
        alert.showError(message);
      }
    },

    async bookNow() {
      if (!this.isLogin) {
        parent.postMessage({ command: 'login' }, '*');
        this.handleScroll();
        return;
      }
      if (this.invalidBetslipId.length > 0) {
        this.handleScroll();
        alert.showError(this.getTranslatedText('invalid betslips'));
        return;
      }
      if (this.betslips.length === 0) return;

      this.isSubmitData = true;
      try {
        const payload = this.buildBetPayload();
        const res = await indexService.bookNow(payload);

        if (res.status === 'success') {
          this.handleBookNowSuccess(res);
        } else {
          this.handleBookNowError(res);
        }
      } catch (error) {
        console.error('Book now error:', error);
      } finally {
        this.isSubmitData = false;
      }
    },

    handleScroll() {
      parent.postMessage({ command: 'scrollIdTop', id: 'modalPlayHorse' }, '*');
      window.parent.postMessage({ type: 'scroll_to_top', top: 0 }, '*');
    },

    handleUpdateTotalStakeMultiple(amount) {
      this.$store.dispatch('handleChangeStakeMultiple', amount);
    },

    handleChangeEachWay(e, item) {
      this.$store.commit('CHANGE_EACH_WAY_BET', { bet: item, eachWayBet: e.target.checked })
    },

    handleChangeEachWayMultip(value) {
      this.$store.dispatch('updateEachWayBetMultiple', value);
    },

    handleUpdateTempAmount(itemId, value) {
      this.$set(this.tempAmount, itemId, value);
    },

    startEditAmount(item) {
      this.$set(this.editingAmount, item.id, true);
      this.$set(this.tempAmount, item.id, fixedNumber(item.amount));
    },

    updateAmountOnBetItem(item) {
      const numValue = parseStakeAmount(this.tempAmount[item.id]);
      if (isStakeInRange(numValue, this.betMin, this.betMax)) {
        const amount = parseStakeAmount(fixedNumber(numValue));
        this.$store.commit('UPDATE_BETSLIP', { bet: item, amount });
      } else {
        this.$set(this.tempAmount, item.id, fixedNumber(item.amount));
      }
      this.$set(this.editingAmount, item.id, false);
    },
  },
};
</script>

<style lang="scss">
@import "@/public/scss/components/right_bar.scss";
</style>
